import React, { useCallback, useEffect, useState } from 'react';
import { Container, Card, Button, Alert, Spinner, Row, Col, Input, Field } from '../../../../core/ui';
import { listCards, createCard, updateCard, deleteCard } from '../../api/budget';
import { formatTry } from '../../format';
import { useTranslation } from '../../../../core/context/TranslationContext';
import ScopeSwitcher from '../../../family/components/ScopeSwitcher';
import { matchBank, getBankIconBackground, resolveCardColor, BankIcon } from './bankIcons';
import styles from './CardManager.module.css';

const emptyDraft = () => ({
  bank: '',
  cardName: '',
  last4: '',
  colorHex: '#5470c6',
  bankIcon: '',
  creditLimit: '',
  currency: 'TRY',
});

const CardManager = () => {
  const { t } = useTranslation();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setCards(await listCards());
    } catch (err) {
      setError(err?.message || t('error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleBankChange = (bank) => {
    const matched = matchBank(bank);
    const update = { bank };
    if (matched) {
      update.bankIcon = matched.key;
      if (draft.colorHex === '#5470c6') {
        update.colorHex = matched.brandColor;
      }
    } else {
      update.bankIcon = '';
    }
    setDraft({ ...draft, ...update });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      await createCard({
        ...draft,
        creditLimit: draft.creditLimit ? Number(draft.creditLimit) : null,
      });
      setDraft(emptyDraft());
      load();
    } catch (err) {
      setError(err?.message || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('budget.confirmDeleteCard'))) return;
    await deleteCard(id);
    load();
  };

  const handleInlinePatch = async (id, field, value) => {
    await updateCard(id, { [field]: value });
    load();
  };

  return (
    <Container>
      <div className={styles.headerRow}>
        <h2 className={styles.heading}>{t('budget.cards')}</h2>
        <ScopeSwitcher
          current="mine"
          mineRoute="/budget/cards"
          familyRoute="/family/cards"
        />
      </div>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Card className={styles.formCard}>
        <Card.Header>{t('budget.addCard')}</Card.Header>
        <Card.Body>
          <form onSubmit={handleCreate}>
            <Row gutter="md" className={styles.formRow}>
              <Col span={3}>
                <Field label={t('budget.bank')}>
                  <div className={styles.bankField}>
                    <span
                      className={styles.bankIconWrapper}
                      style={{ background: getBankIconBackground(draft.bankIcon) || '#5470c633' }}
                    >
                      <BankIcon bankIcon={draft.bankIcon} bankName={draft.bank} width={72} className={styles.bankIconPreview} />
                    </span>
                    <Input
                      placeholder="Garanti, İş Bankası…"
                      value={draft.bank}
                      onChange={(e) => handleBankChange(e.target.value)}
                      required
                    />
                  </div>
                </Field>
              </Col>
              <Col span={3}>
                <Field label={t('budget.cardName')}>
                  <Input
                    placeholder={t('budget.cardName')}
                    value={draft.cardName}
                    onChange={(e) => setDraft({ ...draft, cardName: e.target.value })}
                  />
                </Field>
              </Col>
              <Col span={2}>
                <Field label={t('budget.last4')}>
                  <Input
                    placeholder="1234"
                    maxLength={4}
                    value={draft.last4}
                    onChange={(e) =>
                      setDraft({ ...draft, last4: e.target.value.replace(/\D/g, '').slice(0, 4) })
                    }
                  />
                </Field>
              </Col>
              <Col span={1}>
                <Field label={t('budget.color')}>
                  <Input
                    type="color"
                    value={draft.colorHex}
                    onChange={(e) => setDraft({ ...draft, colorHex: e.target.value })}
                    className={styles.colorInput}
                  />
                </Field>
              </Col>
              <Col span={2}>
                <Field label={t('budget.creditLimit')}>
                  <Input
                    type="number"
                    placeholder="0"
                    value={draft.creditLimit}
                    onChange={(e) => setDraft({ ...draft, creditLimit: e.target.value })}
                  />
                </Field>
              </Col>
              <Col span={1}>
                <Button type="submit" disabled={saving} className={styles.submitBtn}>
                  {saving ? <Spinner size="sm" /> : t('budget.add')}
                </Button>
              </Col>
            </Row>
          </form>
        </Card.Body>
      </Card>

      {loading ? (
        <Spinner label={t('loading')} />
      ) : (
        <Row gutter="md" className={styles.cardsGrid}>
          {cards.map(c => (
            <Col key={c.id}>
              <Card
                className={styles.cardItem}
                style={{ borderTop: `3px solid ${resolveCardColor(c)}` }}
              >
                <Card.Body>
                  <div className={styles.cardHeader}>
                    <div className={styles.cardInfo}>
                      <span
                        className={styles.bankIconWrapper}
                        style={{ background: getBankIconBackground(c.bankIcon) || (c.colorHex || '#5470c6') + '33' }}
                      >
                        <BankIcon bankIcon={c.bankIcon} bankName={c.bank} width={64} className={styles.bankIconDisplay} />
                      </span>
                      <div>
                        <div className={styles.bankName}>{c.bank}</div>
                        {(c.cardName || c.last4) && (
                          <div className={styles.cardDetail}>
                            {c.cardName}{c.last4 && ` · ****${c.last4}`}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="dangerGhost"
                      onClick={() => handleDelete(c.id)}
                    >
                      {t('delete')}
                    </Button>
                  </div>

                  <div>
                    <div className={styles.statLabel}>{t('budget.creditLimit')}</div>
                    <div className={styles.statValue}>{formatTry(c.creditLimit)}</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
          {cards.length === 0 && (
            <Col>
              <Card>
                <Card.Body className={styles.emptyCard}>
                  {t('budget.noCardsYet')}
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      )}

      {handleInlinePatch && null}
    </Container>
  );
};

export default CardManager;